import React from 'react';
import { Link } from 'gatsby-link';
import styled from 'styled-components';

import { ChevronRightSmall } from '../../atoms/icons';
import { ChevronLeft } from '../../atoms/icons/ChevronLeft';

import { DEQ_COMPS, DEQ_TYPES } from '../../../utils/deq';

import mq from '../../../styles/breakpoints';
import colors from '../../../styles/colors';
import Spacings from '../../../styles/Spacings';

import { Theme } from '../../../tokens';

import {
  BreadcrumbItem,
  BreadcrumbsProps,
  StyledNavProps,
} from './Breadcrumbs.types';

const Breadcrumbs = ({ theme, items }: BreadcrumbsProps) => {
  const concatLabelHierarchy = (breadCrumbs: BreadcrumbItem[]) => {
    let labels = '';

    return breadCrumbs.map((item, index) => {
      labels += (labels ? '|' : '') + item.label.toLowerCase();
      return labels;
    });
  };

  return (
    <StyledNav theme={theme}>
      <StyledOrderedList>
        {items.map((item, index) => (
          <ListItem
            {...item}
            key={index}
            elementPosition={index}
            elementHierarchy={concatLabelHierarchy(items)[index]}
          />
        ))}
      </StyledOrderedList>
    </StyledNav>
  );
};

const ListItem = ({
  path,
  label,
  elementPosition,
  elementHierarchy,
}: BreadcrumbItem) => (
  <StyledListItem>
    <ChevronLeft />
    <BreadCrumbLink
      to={path}
      data-component-type={DEQ_COMPS.navigatorBreadcrumb}
      data-element-type={DEQ_TYPES.breadcrumb}
      data-element-label={label?.toLowerCase()}
      data-element-position={elementPosition}
      data-element-hierarchy={elementHierarchy}
      data-element-target={path}
    >
      {label}
    </BreadCrumbLink>
    <ChevronRightSmall />
  </StyledListItem>
);

const BreadCrumbLink = styled(Link)`
  color: inherit;
  margin-right: 10px;
`;

const StyledOrderedList = styled.ol`
  display: inline-flex;
  gap: 10px;
  list-style: none;
  margin: 0;
  padding: 0;
`;

const StyledListItem = styled.li`
  display: none;
  margin: 0;
  padding: 0;

  .chevron-left-icon {
    display: none;
  }

  &:nth-last-child(2) {
    align-items: center;
    display: inline-flex;
    gap: ${Spacings.XXS};

    .chevron-left-icon {
      display: inline;
    }

    .chevron-right-small-icon {
      display: none;
    }

    ${BreadCrumbLink} {
      text-decoration: none;
    }
  }

  &:last-child {
    ${BreadCrumbLink} {
      text-decoration: none;
    }

    .chevron-right-small-icon {
      display: none;
    }
  }

  ${mq.from.breakpoint.M`
    display: inline;

    &:nth-last-child(2) {
      gap: 0;

      .chevron-left-icon {
        display: none;
      }

      .chevron-right-small-icon {
        display: inline;
      }

      ${BreadCrumbLink} {
        text-decoration: underline;
      }
    }
  `}
`;

const StyledNav = styled.nav<StyledNavProps>`
  color: ${({ theme }) =>
    theme === Theme.LIGHT ? colors.white : colors.abnGreen300};
  font-family: ${({ theme }) => theme.fontRegular};
  font-size: 16px;

  ${BreadCrumbLink} {
    text-decoration-color: ${({ theme }: StyledNavProps) =>
      theme === Theme.LIGHT ? colors.white : colors.abnMutedGreen};
  }

  ${mq.from.breakpoint.M`
    font-size: 14px;

    svg path {
      fill: ${({ theme }: StyledNavProps) =>
        theme === Theme.LIGHT ? colors.white : colors.abnNeutral400};
    }

    ${StyledListItem} {
      &:last-child {
        color: ${({ theme }: StyledNavProps) =>
          theme === Theme.LIGHT ? colors.white : colors.abnNeutral400};
      }
    }
  `}
`;

export default Breadcrumbs;
